import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { CustomerDetail } from "../types.js";
import { kpi, sectionHead, delta, nsLink } from "../components.js";
import { money, moneyShort, fmtDate } from "../format.js";

let tab: "sales" | "quotes" = "sales";
let shown = 50;

export const customer: View<CustomerDetail> = {
  title: () => "Customer",
  filters: [],
  async load(_app, param) {
    if (!param) throw new Error("No customer picked.");
    tab = "sales"; shown = 50;
    return api.customer(param);
  },
  render(app: App, d) {
    const ty = d.divisions.reduce((a, x) => a + x.thisYear, 0);
    const ly = d.divisions.reduce((a, x) => a + x.lastYear, 0);
    const open = d.quotes.filter((q) => q.status === "open");
    const decided = d.quotes.filter((q) => q.status !== "open");
    const won = decided.filter((q) => q.status === "processed").length;
    const rows = tab === "sales" ? d.docs : d.quotes;
    return html`
      <div class="row" style="margin-bottom:14px;flex-wrap:wrap;gap:10px;align-items:baseline">
        <h2 style="margin:0;font-size:22px">${d.name}</h2>
        <span class="muted">${d.countyLabel ?? d.state ?? ""}</span>
        <span class="spacer"></span>
        ${nsLink(d.nsUrl, "Open customer in NetSuite")}
      </div>
      <div class="grid g-kpi">
        ${kpi(`${d.year} so far`, moneyShort(ty), html`${ly ? delta(ty, ly) : nothing}<span>vs ${moneyShort(ly)} all of ${d.year - 1}</span>`)}
        ${kpi("Open quotes", moneyShort(open.reduce((a, q) => a + q.amount, 0)), html`<span>${open.length} open</span>`)}
        ${kpi("Quote win rate", decided.length ? `${Math.round((won / decided.length) * 100)}%` : "—", html`<span>${won} won of ${decided.length} decided</span>`)}
        ${kpi("Last purchase", d.docs[0] ? fmtDate(d.docs[0].date) : "—", html`<span>${d.docs[0] ? `${d.docs[0].type} ${d.docs[0].doc}` : "No sales on file"}</span>`)}
      </div>
      ${d.divisions.length ? html`<section class="glass card mt">
        ${sectionHead("By division", `${d.year} so far vs ${d.year - 1} full year`)}
        <div class="table-wrap"><table class="t">
          <thead><tr><th>Division</th><th class="r">${d.year}</th><th class="r">${d.year - 1}</th></tr></thead>
          <tbody>${d.divisions.map((x) => html`<tr><td class="name">${x.name}</td><td class="r">${money(x.thisYear)}</td><td class="r muted">${money(x.lastYear)}</td></tr>`)}</tbody>
        </table></div>
      </section>` : nothing}
      <section class="glass card mt">
        <div class="row" style="margin-bottom:10px;flex-wrap:wrap;gap:10px">
          <div class="seg" role="group" aria-label="Show">
            <button aria-pressed=${tab === "sales"} @click=${() => { tab = "sales"; shown = 50; app.render(); }}>Invoices & credits <span class="muted">${d.docs.length}</span></button>
            <button aria-pressed=${tab === "quotes"} @click=${() => { tab = "quotes"; shown = 50; app.render(); }}>Quotes <span class="muted">${d.quotes.length}</span></button>
          </div>
          <span class="spacer"></span>
          <span class="muted small">Click a document number to open it in NetSuite</span>
        </div>
        ${rows.length === 0 ? html`<div class="empty-state">Nothing here.</div>` : tab === "sales" ? html`
          <div class="table-wrap"><table class="t">
            <thead><tr><th>Document</th><th>Type</th><th>Date</th><th>Rep</th><th>Division</th><th class="r">Amount</th></tr></thead>
            <tbody>${d.docs.slice(0, shown).map((x) => html`<tr>
              <td class="name">${x.nsUrl ? html`<a class="link" href=${x.nsUrl} target="_blank" rel="noopener noreferrer">${x.doc} ↗</a>` : x.doc}</td>
              <td>${x.type}</td><td>${fmtDate(x.date)}</td><td>${x.repName}</td><td class="muted">${x.divisions.join(", ")}</td>
              <td class="r">${money(x.amount)}</td></tr>`)}</tbody>
          </table></div>` : html`
          <div class="table-wrap"><table class="t">
            <thead><tr><th>Quote</th><th>Status</th><th>Date</th><th>Rep</th><th class="r">Amount</th></tr></thead>
            <tbody>${d.quotes.slice(0, shown).map((x) => html`<tr>
              <td class="name">${x.nsUrl ? html`<a class="link" href=${x.nsUrl} target="_blank" rel="noopener noreferrer">${x.doc} ↗</a>` : x.doc}</td>
              <td><span class="status-pill ${x.status}">${x.status === "processed" ? "Won" : x.status}</span></td><td>${fmtDate(x.date)}</td><td>${x.repName}</td>
              <td class="r">${money(x.amount)}</td></tr>`)}</tbody>
          </table></div>`}
        ${rows.length > shown ? html`<div class="row mt" style="justify-content:center"><button class="btn" @click=${() => { shown += 100; app.render(); }}>Show more (${rows.length - shown} left)</button></div>` : nothing}
      </section>
      <p class="muted small mt">Shows what's in your view only. NetSuite opens in a new tab and uses your own NetSuite login.</p>`;
  },
};
